import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowLeft,
  Phone,
  Mail,
  Laptop,
  Shield,
  ShieldOff,
  Clock,
  User,
  FileText,
  Wrench,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  CreditCard,
  Euro,
  Download,
  Loader2,
  Receipt,
  Building,
  Package,
  RefreshCw,
  Camera,
  X
} from "lucide-react";

const statusConfig = {
  appareil_depose: { label: "Appareil déposé", color: "bg-blue-100 text-blue-800" },
  en_attente_diagnostic: { label: "En attente diagnostic", color: "bg-yellow-100 text-yellow-800" },
  diagnostic_en_cours: { label: "Diagnostic en cours", color: "bg-yellow-100 text-yellow-800" },
  en_attente_devis: { label: "En attente devis", color: "bg-purple-100 text-purple-800" },
  devis_envoye: { label: "Devis envoyé", color: "bg-purple-100 text-purple-800" },
  devis_accepte: { label: "Devis accepté", color: "bg-green-100 text-green-800" },
  devis_refuse: { label: "Devis refusé", color: "bg-red-100 text-red-800" },
  en_attente_pieces: { label: "En attente pièces", color: "bg-orange-100 text-orange-800" },
  reparation_en_cours: { label: "Réparation en cours", color: "bg-orange-100 text-orange-800" },
  en_attente_constructeur: { label: "En attente constructeur", color: "bg-indigo-100 text-indigo-800" },
  avoir_accorde: { label: "Avoir accordé", color: "bg-emerald-100 text-emerald-800" },
  echange_accorde: { label: "Échange accordé", color: "bg-teal-100 text-teal-800" },
  test_final: { label: "Test final", color: "bg-cyan-100 text-cyan-800" },
  pret_a_restituer: { label: "Prêt à restituer", color: "bg-green-100 text-green-800" },
  restitue: { label: "Restitué", color: "bg-gray-100 text-gray-800" },
  cloture: { label: "Clôturé", color: "bg-gray-100 text-gray-800" },
  refus_garantie: { label: "Garantie refusée", color: "bg-red-100 text-red-800" },
  irreparable: { label: "Irréparable", color: "bg-red-100 text-red-800" },
  sans_suite: { label: "Sans suite", color: "bg-gray-100 text-gray-800" }
};

const allStatuses = Object.keys(statusConfig);

const SAVCaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [savCase, setSavCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Dialogs
  const [diagnosticDialog, setDiagnosticDialog] = useState(false);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  
  // Diagnostic form
  const [diagnosticData, setDiagnosticData] = useState({
    tests_realises: [],
    panne_detectee: "",
    origine_probable: "",
    niveau_gravite: "moyen",
    reparabilite: "reparable",
    pieces_necessaires: [],
    temps_estime: "",
    notes_techniques: ""
  });
  const [diagnosticPhotos, setDiagnosticPhotos] = useState([]);
  
  // Technicians
  const [technicians, setTechnicians] = useState([]);
  
  // Quote form
  const [quoteLines, setQuoteLines] = useState([{ description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 }]);
  const [quoteRemise, setQuoteRemise] = useState(0);
  
  // Payment form
  const [paymentData, setPaymentData] = useState({ type: "acompte", montant: 0 });

  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [invoicePaymentMode, setInvoicePaymentMode] = useState("cb");
  const [adminExtraLines, setAdminExtraLines] = useState([]);
  const [showGarantieForm, setShowGarantieForm] = useState(false);
  const [garantieData, setGarantieData] = useState({
    constructeur: "", numero_rma: "", statut_garantie: "en_cours",
    resolution: "", montant_avoir: "", numero_avoir: "", date_avoir: "",
    numero_serie_remplacement: "", date_reception_remplacement: "",
    remis_a_bv: false, date_remise_bv: "", notes_garantie: ""
  });
  const [savingGarantie, setSavingGarantie] = useState(false);

  const downloadPdf = async (url, filename) => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    setDownloadingPdf(url);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erreur téléchargement");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Erreur lors du téléchargement du PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchCase = async () => {
    try {
      const response = await API.get(`/sav-cases/${id}`);
      setSavCase(response.data);
    } catch (error) {
      toast.error("Dossier non trouvé");
      navigate("/sav");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCase();
    const fetchTechs = async () => {
      try {
        const res = await API.get("/users/technicians");
        setTechnicians(res.data);
      } catch (e) { /* ignore */ }
    };
    fetchTechs();
  }, [id]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await API.put(`/sav-cases/${id}`, { statut: newStatus });
      toast.success("Statut mis à jour");
      fetchCase();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const assignTechnician = async (techId) => {
    try {
      await API.put(`/sav-cases/${id}`, { technicien_id: techId === "none" ? "" : techId });
      toast.success("Technicien assigné");
      fetchCase();
    } catch (error) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  const submitDiagnostic = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post("/diagnostics", { ...diagnosticData, sav_case_id: id });
      const diagId = res.data?.id;
      
      // Upload photos if any (don't block if photo upload fails)
      if (diagId && diagnosticPhotos.length > 0) {
        try {
          const formData = new FormData();
          for (const file of diagnosticPhotos) {
            formData.append("files", file);
          }
          await API.post(`/diagnostics/${diagId}/photos`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        } catch (photoErr) {
          toast.error("Diagnostic enregistré mais erreur upload photo. Réessayez depuis le dossier.");
        }
      }
      
      toast.success("Diagnostic enregistré");
      setDiagnosticDialog(false);
      setDiagnosticPhotos([]);
      fetchCase();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement du diagnostic");
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    try {
      if (editingQuoteId) {
        // Update existing quote
        await API.put(`/quotes/${editingQuoteId}`, {
          lignes: quoteLines,
          remise_globale: quoteRemise
        });
        toast.success("Devis modifié");
      } else {
        // Create new quote
        await API.post("/quotes", {
          sav_case_id: id,
          lignes: quoteLines,
          remise_globale: quoteRemise
        });
        toast.success("Devis créé");
      }
      setQuoteDialog(false);
      setEditingQuoteId(null);
      fetchCase();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const openEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    setQuoteLines(quote.lignes.map(l => ({
      description: l.description || "",
      type: l.type || "piece",
      quantite: l.quantite || 1,
      prix_unitaire: l.prix_unitaire || 0,
      remise: l.remise || 0
    })));
    setQuoteRemise(quote.remise_globale || 0);
    setQuoteDialog(true);
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await API.put(`/quotes/${quoteId}`, { statut: status });
      if (status === "envoye") {
        toast.success("Devis envoyé par email au client");
      } else {
        toast.success("Devis mis à jour");
      }
      fetchCase();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const convertQuoteToInvoice = async (quoteId) => {
    try {
      const payload = { mode_paiement: invoicePaymentMode };
      if (adminExtraLines.length > 0) {
        payload.lignes_supplementaires = adminExtraLines.filter(l => l.description && l.prix_unitaire > 0);
      }
      const response = await API.post(`/quotes/${quoteId}/convert-to-invoice`, payload);
      const numero = response.data?.numero || "";
      toast.success(`Facture ${numero} créée`);
      setConvertingQuoteId(null);
      setAdminExtraLines([]);
      fetchCase();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la conversion");
    }
  };

  // Load garantie data when case is loaded
  useEffect(() => {
    if (savCase?.garantie_constructeur) {
      const g = savCase.garantie_constructeur;
      setGarantieData({
        constructeur: g.constructeur || "",
        numero_rma: g.numero_rma || "",
        statut_garantie: g.statut_garantie || "en_cours",
        resolution: g.resolution || "",
        montant_avoir: g.montant_avoir || "",
        numero_avoir: g.numero_avoir || "",
        date_avoir: g.date_avoir || "",
        numero_serie_remplacement: g.numero_serie_remplacement || "",
        date_reception_remplacement: g.date_reception_remplacement || "",
        remis_a_bv: g.remis_a_bv || false,
        date_remise_bv: g.date_remise_bv || "",
        notes_garantie: g.notes_garantie || ""
      });
    }
  }, [savCase]);

  const saveGarantie = async () => {
    setSavingGarantie(true);
    try {
      const data = { ...garantieData };
      if (data.montant_avoir) data.montant_avoir = parseFloat(data.montant_avoir);
      else delete data.montant_avoir;
      
      await API.put(`/sav-cases/${id}/garantie`, data);
      toast.success("Garantie constructeur mise à jour");
      setShowGarantieForm(false);
      fetchCase();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingGarantie(false);
    }
  };




  const createPayment = async (e) => {
    e.preventDefault();
    try {
      const response = await API.post("/payments/create-checkout", {
        ...paymentData,
        reference_id: id,
        mode_paiement: "stripe"
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
    }
  };

  const addQuoteLine = () => {
    setQuoteLines([...quoteLines, { description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 }]);
  };

  const updateQuoteLine = (index, field, value) => {
    const updated = [...quoteLines];
    updated[index][field] = value;
    setQuoteLines(updated);
  };

  const removeQuoteLine = (index) => {
    setQuoteLines(quoteLines.filter((_, i) => i !== index));
  };

  const calculateQuoteTotal = () => {
    const subtotal = quoteLines.reduce((sum, line) => {
      return sum + (line.prix_unitaire * line.quantite * (1 - line.remise / 100));
    }, 0);
    return subtotal * (1 - quoteRemise / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!savCase) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <p className="font-medium text-lg mb-2">Dossier introuvable</p>
        <p className="text-muted-foreground mb-4">Ce dossier SAV n'existe pas ou a été supprimé.</p>
        <Button onClick={() => navigate("/sav")}>Retour aux dossiers</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sav-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/sav")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-mono">{savCase.numero}</h1>
            <Badge className={statusConfig[savCase.statut]?.color}>
              {statusConfig[savCase.statut]?.label}
            </Badge>
            <Badge variant={savCase.type_prise_en_charge === "garantie" ? "default" : "secondary"}>
              {savCase.type_prise_en_charge === "garantie" ? (
                <><Shield className="h-3 w-3 mr-1" /> Garantie</>
              ) : (
                <><ShieldOff className="h-3 w-3 mr-1" /> Hors garantie</>
              )}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Créé le {new Date(savCase.created_at).toLocaleDateString("fr-FR")}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <select
              onChange={(e) => { if (e.target.value) { updateStatus(e.target.value); e.target.value = ""; } }}
              disabled={updating}
              className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              data-testid="status-select"
              defaultValue=""
            >
              <option value="" disabled>Changer le statut</option>
              {allStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusConfig[status].label}
                </option>
              ))}
            </select>
            
            {technicians.length > 0 && (
              <select
                value={savCase?.technicien_id || "none"}
                onChange={(e) => assignTechnician(e.target.value)}
                className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="assign-technician-select"
              >
                <option value="none">Non assigné</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.prenom} {tech.nom}
                  </option>
                ))}
              </select>
            )}
            
            <Dialog open={diagnosticDialog} onOpenChange={setDiagnosticDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-diagnostic-btn">
                  <Wrench className="h-4 w-4 mr-2" />
                  Diagnostic
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <form onSubmit={submitDiagnostic}>
                  <DialogHeader>
                    <DialogTitle>Nouveau diagnostic</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>Panne détectée *</Label>
                      <Textarea
                        value={diagnosticData.panne_detectee}
                        onChange={(e) => setDiagnosticData({ ...diagnosticData, panne_detectee: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Niveau de gravité</Label>
                        <select
                          value={diagnosticData.niveau_gravite}
                          onChange={(e) => setDiagnosticData({ ...diagnosticData, niveau_gravite: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="faible">Faible</option>
                          <option value="moyen">Moyen</option>
                          <option value="eleve">Élevé</option>
                          <option value="critique">Critique</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Réparabilité</Label>
                        <select
                          value={diagnosticData.reparabilite}
                          onChange={(e) => setDiagnosticData({ ...diagnosticData, reparabilite: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="reparable">Réparable</option>
                          <option value="difficilement_reparable">Difficilement réparable</option>
                          <option value="irreparable">Irréparable</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Origine probable</Label>
                      <Input
                        value={diagnosticData.origine_probable}
                        onChange={(e) => setDiagnosticData({ ...diagnosticData, origine_probable: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Temps estimé</Label>
                      <Input
                        value={diagnosticData.temps_estime}
                        onChange={(e) => setDiagnosticData({ ...diagnosticData, temps_estime: e.target.value })}
                        placeholder="Ex: 2h, 1 jour..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes techniques</Label>
                      <Textarea
                        value={diagnosticData.notes_techniques}
                        onChange={(e) => setDiagnosticData({ ...diagnosticData, notes_techniques: e.target.value })}
                      />
                    </div>
                    {/* Photos upload */}
                    <div className="space-y-2">
                      <Label>Photos (max 3)</Label>
                      <div className="flex flex-wrap gap-2 items-center">
                        {diagnosticPhotos.map((file, idx) => (
                          <div key={idx} className="relative group">
                            <img src={URL.createObjectURL(file)} alt={`Photo ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
                            <button type="button" onClick={() => setDiagnosticPhotos(diagnosticPhotos.filter((_, i) => i !== idx))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        {diagnosticPhotos.length < 3 && (
                          <label className="h-20 w-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                            <Camera className="h-5 w-5 text-muted-foreground/50" />
                            <span className="text-[10px] text-muted-foreground mt-0.5">Ajouter</span>
                            <input type="file" accept="image/*" className="hidden" multiple onChange={(e) => {
                              if (e.target.files?.length) {
                                const newFiles = Array.from(e.target.files).slice(0, 3 - diagnosticPhotos.length);
                                setDiagnosticPhotos([...diagnosticPhotos, ...newFiles]);
                              }
                              e.target.value = "";
                            }} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit">Enregistrer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Bon de dépôt PDF */}
            <button
              onClick={() => downloadPdf(`/api/pdf/bon-depot/${id}`, `BonDepot_${savCase?.numero}.pdf`)}
              disabled={downloadingPdf === `/api/pdf/bon-depot/${id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
              data-testid="download-bon-depot-btn"
            >
              {downloadingPdf === `/api/pdf/bon-depot/${id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Bon de dépôt
            </button>
            
            <Dialog open={quoteDialog} onOpenChange={(open) => { setQuoteDialog(open); if (!open) setEditingQuoteId(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-quote-btn" onClick={() => { setEditingQuoteId(null); setQuoteLines([{ description: "", type: "piece", quantite: 1, prix_unitaire: 0, remise: 0 }]); setQuoteRemise(0); }}>
                  <FileText className="h-4 w-4 mr-2" />
                  Créer devis
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <form onSubmit={submitQuote}>
                  <DialogHeader>
                    <DialogTitle>{editingQuoteId ? "Modifier le devis" : "Nouveau devis"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {quoteLines.map((line, index) => (
                      <div key={index} className="p-3 rounded-lg border bg-secondary/20 space-y-3" data-testid={`sav-quote-line-${index}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Ligne {index + 1}</span>
                          {quoteLines.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeQuoteLine(index)} className="h-8 w-8 p-0 text-destructive">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Input
                            placeholder="Description"
                            value={line.description}
                            onChange={(e) => updateQuoteLine(index, "description", e.target.value)}
                            required
                          />
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <select
                              value={line.type}
                              onChange={(e) => updateQuoteLine(index, "type", e.target.value)}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <option value="piece">Pièce</option>
                              <option value="main_oeuvre">Main d'œuvre</option>
                              <option value="forfait">Forfait</option>
                            </select>
                            <Input
                              type="number"
                              min="1"
                              placeholder="Qté"
                              value={line.quantite}
                              onChange={(e) => updateQuoteLine(index, "quantite", parseInt(e.target.value) || 1)}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Prix unit."
                              value={line.prix_unitaire || ""}
                              onChange={(e) => updateQuoteLine(index, "prix_unitaire", parseFloat(e.target.value) || 0)}
                            />
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="Remise %"
                              value={line.remise || ""}
                              onChange={(e) => updateQuoteLine(index, "remise", parseFloat(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addQuoteLine} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une ligne
                    </Button>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Label>Remise globale %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={quoteRemise || ""}
                          onChange={(e) => setQuoteRemise(parseFloat(e.target.value) || 0)}
                          className="w-20"
                        />
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total TTC</p>
                        <p className="text-2xl font-bold">{calculateQuoteTotal().toFixed(2)}€</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground italic px-1 py-2 border-t mt-2">
                    Les réparations seront effectuées après validation du devis. La remise en fonctionnement de l'appareil ne peut être garantie avant intervention.
                  </p>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setQuoteDialog(false)}>
                      Annuler
                    </Button>
                    <Button type="submit">{editingQuoteId ? "Enregistrer les modifications" : "Créer le devis"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-payment-btn">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Paiement Stripe
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={createPayment}>
                  <DialogHeader>
                    <DialogTitle>Créer un paiement</DialogTitle>
                    <DialogDescription>Paiement sécurisé via Stripe</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                      <Label>Type de paiement</Label>
                      <select
                        value={paymentData.type}
                        onChange={(e) => setPaymentData({ ...paymentData, type: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="acompte">Acompte</option>
                        <option value="solde">Solde</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.50"
                        value={paymentData.montant}
                        onChange={(e) => setPaymentData({ ...paymentData, montant: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit">Procéder au paiement</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Admin Comment */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Commentaire admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Existing comments */}
          {savCase.commentaires_admin?.length > 0 && (
            <div className="space-y-3 mb-4">
              {savCase.commentaires_admin.map((c, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-muted/50 border text-sm">
                  <p>{c.texte}</p>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {c.auteur} — {new Date(c.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={async (e) => {
            e.preventDefault();
            const input = e.target.elements.commentaire;
            if (!input.value.trim()) return;
            try {
              await API.put(`/sav-cases/${id}/commentaire`, { texte: input.value.trim() });
              toast.success("Commentaire ajouté");
              input.value = "";
              fetchCase();
            } catch { toast.error("Erreur"); }
          }} className="flex gap-2">
            <Textarea name="commentaire" placeholder="Ajouter un commentaire..." rows={1} className="min-h-[40px] flex-1" data-testid="admin-comment-input" />
            <Button type="submit" size="sm" className="self-end" data-testid="submit-comment-btn">Envoyer</Button>
          </form>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Client & Device Info */}
        <div className="space-y-6">
          {/* Client Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold">{savCase.client?.prenom} {savCase.client?.nom}</p>
              {savCase.client?.telephone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {savCase.client.telephone}
                </div>
              )}
              {savCase.client?.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {savCase.client.email}
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/clients/${savCase.client_id}`)}>
                Voir le profil
              </Button>
            </CardContent>
          </Card>

          {/* Device Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Laptop className="h-5 w-5" />
                Appareil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-semibold">{savCase.device?.marque} {savCase.device?.modele}</p>
              <Badge variant="secondary" className="capitalize">
                {savCase.device?.categorie?.replace(/_/g, " ")}
              </Badge>
              {savCase.device?.numero_serie && (
                <div className="text-sm">
                  <span className="text-muted-foreground">N° série: </span>
                  <span className="font-mono">{savCase.device.numero_serie}</span>
                </div>
              )}
              {savCase.device?.imei && (
                <div className="text-sm">
                  <span className="text-muted-foreground">IMEI: </span>
                  <span className="font-mono">{savCase.device.imei}</span>
                </div>
              )}
              <Separator />
              <div>
                <p className="text-sm font-medium mb-1">Panne déclarée</p>
                <p className="text-sm text-muted-foreground">{savCase.device?.panne_declaree}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Diagnostics */}
          {savCase.diagnostics?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Diagnostics</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {savCase.diagnostics.map((diag, index) => (
                    <AccordionItem key={diag.id} value={diag.id}>
                      <AccordionTrigger>
                        <div className="flex items-center gap-3 text-left">
                          <Badge variant={
                            diag.reparabilite === "reparable" ? "default" :
                            diag.reparabilite === "difficilement_reparable" ? "secondary" : "destructive"
                          }>
                            {diag.reparabilite?.replace(/_/g, " ")}
                          </Badge>
                          <span className="truncate">{diag.panne_detectee?.substring(0, 50)}...</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="space-y-3">
                        <div>
                          <p className="text-sm font-medium">Panne détectée</p>
                          <p className="text-sm text-muted-foreground">{diag.panne_detectee}</p>
                        </div>
                        {diag.origine_probable && (
                          <div>
                            <p className="text-sm font-medium">Origine probable</p>
                            <p className="text-sm text-muted-foreground">{diag.origine_probable}</p>
                          </div>
                        )}
                        <div className="flex gap-4">
                          <div>
                            <p className="text-sm font-medium">Gravité</p>
                            <Badge variant="outline">{diag.niveau_gravite}</Badge>
                          </div>
                          {diag.temps_estime && (
                            <div>
                              <p className="text-sm font-medium">Temps estimé</p>
                              <p className="text-sm">{diag.temps_estime}</p>
                            </div>
                          )}
                        </div>
                        {diag.notes_techniques && (
                          <div>
                            <p className="text-sm font-medium">Notes techniques</p>
                            <p className="text-sm text-muted-foreground">{diag.notes_techniques}</p>
                          </div>
                        )}
                        {diag.photos?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium mb-2">Photos</p>
                            <div className="flex flex-wrap gap-2">
                              {diag.photos.map((photo, pIdx) => (
                                <a key={pIdx} href={`${process.env.REACT_APP_BACKEND_URL}${photo}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={`Photo ${pIdx + 1}`} className="h-24 w-24 object-cover rounded-lg border hover:opacity-80 transition-opacity" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          )}

          {/* Garantie Constructeur */}
          {savCase.type_prise_en_charge === "garantie" && (
            <Card className="border-indigo-200" data-testid="garantie-constructeur-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="h-5 w-5 text-indigo-600" />
                    Garantie Constructeur
                  </CardTitle>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadPdf(`/api/pdf/rapport-intervention/${id}`, `Rapport_${savCase.numero}.pdf`)}
                      disabled={downloadingPdf === `/api/pdf/rapport-intervention/${id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border hover:bg-accent disabled:opacity-50"
                      data-testid="download-rapport-btn"
                    >
                      {downloadingPdf === `/api/pdf/rapport-intervention/${id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Rapport
                    </button>
                    <Button size="sm" variant={showGarantieForm ? "secondary" : "outline"} onClick={() => setShowGarantieForm(!showGarantieForm)} data-testid="edit-garantie-btn">
                      {showGarantieForm ? "Fermer" : (savCase.garantie_constructeur?.constructeur ? "Modifier" : "Renseigner")}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Display current garantie info */}
                {savCase.garantie_constructeur?.constructeur && !showGarantieForm && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground">Constructeur</p>
                        <p className="font-medium">{savCase.garantie_constructeur.constructeur}</p>
                      </div>
                      {savCase.garantie_constructeur.numero_rma && (
                        <div>
                          <p className="text-muted-foreground">N° RMA</p>
                          <p className="font-mono font-medium">{savCase.garantie_constructeur.numero_rma}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-muted-foreground">Statut</p>
                        <Badge className={
                          savCase.garantie_constructeur.statut_garantie === "avoir_accorde" ? "bg-emerald-100 text-emerald-800" :
                          savCase.garantie_constructeur.statut_garantie === "echange_accorde" ? "bg-teal-100 text-teal-800" :
                          savCase.garantie_constructeur.statut_garantie === "refuse" ? "bg-red-100 text-red-800" :
                          "bg-indigo-100 text-indigo-800"
                        }>
                          {{en_cours: "En cours", en_attente_constructeur: "Attente constructeur", avoir_accorde: "Avoir accordé", echange_accorde: "Échange accordé", reparation_garantie: "Réparation garantie", refuse: "Refusé"}[savCase.garantie_constructeur.statut_garantie] || savCase.garantie_constructeur.statut_garantie}
                        </Badge>
                      </div>
                      {savCase.garantie_constructeur.resolution && (
                        <div>
                          <p className="text-muted-foreground">Résolution</p>
                          <Badge variant="outline">
                            {{avoir: "Avoir", echange: "Échange", reparation_garantie: "Réparation garantie", refuse: "Refusé"}[savCase.garantie_constructeur.resolution] || savCase.garantie_constructeur.resolution}
                          </Badge>
                        </div>
                      )}
                    </div>
                    {savCase.garantie_constructeur.montant_avoir > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm">
                        <p className="font-medium text-emerald-800">Avoir : {savCase.garantie_constructeur.montant_avoir.toFixed(2)} EUR</p>
                        {savCase.garantie_constructeur.numero_avoir && <p className="text-emerald-600">N° avoir : {savCase.garantie_constructeur.numero_avoir}</p>}
                      </div>
                    )}
                    {savCase.garantie_constructeur.numero_serie_remplacement && (
                      <div className="bg-teal-50 border border-teal-200 p-3 rounded-lg text-sm">
                        <p className="font-medium text-teal-800">Appareil de remplacement</p>
                        <p className="text-teal-600">N° série : {savCase.garantie_constructeur.numero_serie_remplacement}</p>
                        {savCase.garantie_constructeur.remis_a_bv ? (
                          <Badge className="bg-green-100 text-green-800 mt-1">Remis à Bureau Vallée {savCase.garantie_constructeur.date_remise_bv ? `le ${new Date(savCase.garantie_constructeur.date_remise_bv).toLocaleDateString("fr-FR")}` : ""}</Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-800 mt-1">En attente de remise à BV</Badge>
                        )}
                      </div>
                    )}
                    {savCase.garantie_constructeur.notes_garantie && (
                      <p className="text-sm text-muted-foreground">{savCase.garantie_constructeur.notes_garantie}</p>
                    )}
                  </div>
                )}

                {/* Edit form */}
                {showGarantieForm && (
                  <div className="space-y-4" data-testid="garantie-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Constructeur / Fournisseur</Label>
                        <select value={garantieData.constructeur} onChange={(e) => setGarantieData({...garantieData, constructeur: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                          <option value="">Sélectionner</option>
                          {["HP", "Canon", "Epson", "Samsung", "Lenovo", "Brother", "Acer", "Asus", "Dell", "Apple", "Xiaomi", "Huawei", "Autre"].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>N° RMA / Dossier constructeur</Label>
                        <Input value={garantieData.numero_rma} onChange={(e) => setGarantieData({...garantieData, numero_rma: e.target.value})} placeholder="Ex: RMA-2026-xxxxx" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Statut garantie</Label>
                        <select value={garantieData.statut_garantie} onChange={(e) => setGarantieData({...garantieData, statut_garantie: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                          <option value="en_cours">En cours</option>
                          <option value="en_attente_constructeur">En attente constructeur</option>
                          <option value="avoir_accorde">Avoir accordé</option>
                          <option value="echange_accorde">Échange accordé</option>
                          <option value="reparation_garantie">Réparation sous garantie</option>
                          <option value="refuse">Refusé par le constructeur</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Résolution</Label>
                        <select value={garantieData.resolution} onChange={(e) => setGarantieData({...garantieData, resolution: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                          <option value="">Non définie</option>
                          <option value="avoir">Avoir</option>
                          <option value="echange">Échange / Remplacement</option>
                          <option value="reparation_garantie">Réparation sous garantie</option>
                          <option value="refuse">Refusé</option>
                        </select>
                      </div>
                    </div>

                    {/* Avoir fields */}
                    {(garantieData.resolution === "avoir" || garantieData.statut_garantie === "avoir_accorde") && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="space-y-1.5">
                          <Label>Montant avoir (EUR)</Label>
                          <Input type="number" step="0.01" value={garantieData.montant_avoir} onChange={(e) => setGarantieData({...garantieData, montant_avoir: e.target.value})} placeholder="0.00" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>N° avoir</Label>
                          <Input value={garantieData.numero_avoir} onChange={(e) => setGarantieData({...garantieData, numero_avoir: e.target.value})} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Date avoir</Label>
                          <Input type="date" value={garantieData.date_avoir} onChange={(e) => setGarantieData({...garantieData, date_avoir: e.target.value})} />
                        </div>
                      </div>
                    )}

                    {/* Échange fields */}
                    {(garantieData.resolution === "echange" || garantieData.statut_garantie === "echange_accorde") && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                        <div className="space-y-1.5">
                          <Label>N° série nouvel appareil</Label>
                          <Input value={garantieData.numero_serie_remplacement} onChange={(e) => setGarantieData({...garantieData, numero_serie_remplacement: e.target.value})} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Date réception remplacement</Label>
                          <Input type="date" value={garantieData.date_reception_remplacement} onChange={(e) => setGarantieData({...garantieData, date_reception_remplacement: e.target.value})} />
                        </div>
                        <div className="space-y-1.5 flex items-end gap-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={garantieData.remis_a_bv} onChange={(e) => setGarantieData({...garantieData, remis_a_bv: e.target.checked, date_remise_bv: e.target.checked ? new Date().toISOString().split("T")[0] : ""})} className="rounded" />
                            <span className="text-sm font-medium">Remis à Bureau Vallée</span>
                          </label>
                        </div>
                        {garantieData.remis_a_bv && (
                          <div className="space-y-1.5">
                            <Label>Date remise à BV</Label>
                            <Input type="date" value={garantieData.date_remise_bv} onChange={(e) => setGarantieData({...garantieData, date_remise_bv: e.target.value})} />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label>Notes garantie</Label>
                      <Textarea value={garantieData.notes_garantie} onChange={(e) => setGarantieData({...garantieData, notes_garantie: e.target.value})} placeholder="Informations complémentaires..." rows={2} />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={saveGarantie} disabled={savingGarantie} data-testid="save-garantie-btn">
                        {savingGarantie ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Enregistrement...</> : "Enregistrer"}
                      </Button>
                      <Button variant="outline" onClick={() => setShowGarantieForm(false)}>Annuler</Button>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!savCase.garantie_constructeur?.constructeur && !showGarantieForm && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucune information constructeur renseignée.
                    <button onClick={() => setShowGarantieForm(true)} className="text-primary ml-1 hover:underline">Ajouter</button>
                  </p>
                )}
              </CardContent>
            </Card>
          )}



          {/* Quotes */}
          {savCase.quotes?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Devis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {savCase.quotes.map((quote) => (
                  <div key={quote.id} className="p-4 rounded-lg border bg-secondary/20">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono font-medium">{quote.numero}</span>
                      <Badge className={
                        quote.statut === "accepte" ? "bg-green-100 text-green-800" :
                        quote.statut === "refuse" ? "bg-red-100 text-red-800" :
                        "bg-yellow-100 text-yellow-800"
                      }>
                        {quote.statut}
                      </Badge>
                    </div>
                    <div className="space-y-1 mb-3">
                      {quote.lignes?.map((ligne, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span>{ligne.description}</span>
                          <span className="font-mono">{ligne.total?.toFixed(2)}€</span>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between font-medium">
                      <span>Total TTC</span>
                      <span className="font-mono text-lg">{quote.total_ttc?.toFixed(2)}€</span>
                    </div>
                    {quote.statut === "brouillon" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => openEditQuote(quote)}>
                          Modifier
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateQuoteStatus(quote.id, "envoye")}>
                          Envoyer au client
                        </Button>
                        <button onClick={() => downloadPdf(`/api/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)} disabled={downloadingPdf === `/api/pdf/devis/${quote.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50" data-testid={`download-devis-${quote.id}`}>
                          {downloadingPdf === `/api/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                        </button>
                      </div>
                    )}
                    {quote.statut === "envoye" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => openEditQuote(quote)}>
                          Modifier
                        </Button>
                        <Button size="sm" onClick={() => updateQuoteStatus(quote.id, "accepte")}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accepter
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => updateQuoteStatus(quote.id, "refuse")}>
                          <XCircle className="h-4 w-4 mr-1" />
                          Refuser
                        </Button>
                        <button onClick={() => downloadPdf(`/api/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)} disabled={downloadingPdf === `/api/pdf/devis/${quote.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50" data-testid={`download-devis-${quote.id}`}>
                          {downloadingPdf === `/api/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                        </button>
                      </div>
                    )}
                    {(quote.statut === "accepte" || quote.statut === "refuse") && (
                      <div className="flex gap-2 mt-3">
                        {quote.statut === "accepte" && !quote.facture_numero && user?.role === "admin" && (
                          <Button size="sm" onClick={() => { setConvertingQuoteId(quote.id); setInvoicePaymentMode("cb"); }} data-testid={`invoice-quote-${quote.id}`}>
                            <Receipt className="h-4 w-4 mr-1" />
                            Facturer
                          </Button>
                        )}
                        {quote.facture_numero && (
                          <Badge className="bg-indigo-100 text-indigo-700 self-center">{quote.facture_numero}</Badge>
                        )}
                        <button onClick={() => downloadPdf(`/api/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)} disabled={downloadingPdf === `/api/pdf/devis/${quote.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50" data-testid={`download-devis-${quote.id}`}>
                          {downloadingPdf === `/api/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {savCase.historique?.slice().reverse().map((entry, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                      {index < savCase.historique.length - 1 && (
                        <div className="w-px flex-1 bg-border"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {statusConfig[entry.statut]?.label || entry.statut}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleString("fr-FR")}
                        </span>
                      </div>
                      {entry.commentaire && (
                        <p className="text-sm text-muted-foreground mt-1">{entry.commentaire}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Convert Quote to Invoice Modal (native - Safari safe) */}
      {convertingQuoteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setConvertingQuoteId(null); setAdminExtraLines([]); }}>
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sav-invoice-modal">
            <h3 className="text-lg font-semibold mb-1">Convertir en facture</h3>
            {(() => {
              const quote = (savCase.quotes || []).find(q => q.id === convertingQuoteId);
              const quoteTotal = quote?.total_ttc || 0;
              const extraTotal = adminExtraLines.reduce((sum, l) => sum + ((l.quantite || 1) * (l.prix_unitaire || 0)), 0);
              return (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Devis {quote?.numero} — {quoteTotal.toFixed(2)} EUR
                    {extraTotal > 0 && <span className="text-primary font-medium"> + {extraTotal.toFixed(2)} EUR marge = {(quoteTotal + extraTotal).toFixed(2)} EUR</span>}
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Mode de paiement</label>
                      <select
                        value={invoicePaymentMode}
                        onChange={(e) => setInvoicePaymentMode(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="sav-invoice-payment-select"
                      >
                        <option value="cb">CB</option>
                        <option value="especes">Espèces</option>
                        <option value="cheque">Chèque</option>
                        <option value="virement">Virement</option>
                        <option value="en_attente">En attente de paiement</option>
                      </select>
                    </div>

                    {/* Admin extra lines */}
                    {user?.role === "admin" && (
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Lignes supplémentaires (marge admin)</label>
                          <button
                            type="button"
                            onClick={() => setAdminExtraLines([...adminExtraLines, { description: "", quantite: 1, prix_unitaire: 0 }])}
                            className="text-xs text-primary hover:underline font-medium"
                            data-testid="add-admin-line-btn"
                          >
                            + Ajouter une ligne
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">Ces lignes seront ajoutées à la facture uniquement. Le devis technicien reste inchangé.</p>
                        {adminExtraLines.map((line, idx) => (
                          <div key={idx} className="flex gap-2 items-end">
                            <div className="flex-1">
                              {idx === 0 && <label className="text-xs text-muted-foreground">Désignation</label>}
                              <Input
                                value={line.description}
                                onChange={(e) => {
                                  const updated = [...adminExtraLines];
                                  updated[idx].description = e.target.value;
                                  setAdminExtraLines(updated);
                                }}
                                placeholder="Ex: Marge atelier, Frais de gestion..."
                                className="h-9 text-sm"
                                data-testid={`admin-line-desc-${idx}`}
                              />
                            </div>
                            <div className="w-16">
                              {idx === 0 && <label className="text-xs text-muted-foreground">Qté</label>}
                              <Input
                                type="number"
                                min="1"
                                value={line.quantite}
                                onChange={(e) => {
                                  const updated = [...adminExtraLines];
                                  updated[idx].quantite = parseInt(e.target.value) || 1;
                                  setAdminExtraLines(updated);
                                }}
                                className="h-9 text-sm"
                              />
                            </div>
                            <div className="w-24">
                              {idx === 0 && <label className="text-xs text-muted-foreground">Prix EUR</label>}
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={line.prix_unitaire}
                                onChange={(e) => {
                                  const updated = [...adminExtraLines];
                                  updated[idx].prix_unitaire = parseFloat(e.target.value) || 0;
                                  setAdminExtraLines(updated);
                                }}
                                className="h-9 text-sm"
                                data-testid={`admin-line-price-${idx}`}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setAdminExtraLines(adminExtraLines.filter((_, i) => i !== idx))}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" className="flex-1" onClick={() => { setConvertingQuoteId(null); setAdminExtraLines([]); }}>
                        Annuler
                      </Button>
                      <Button className="flex-1" onClick={() => convertQuoteToInvoice(convertingQuoteId)} data-testid="sav-confirm-invoice-btn">
                        <Receipt className="h-4 w-4 mr-2" />
                        Facturer {extraTotal > 0 ? `(${(quoteTotal + extraTotal).toFixed(2)} EUR)` : ""}
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SAVCaseDetailPage;
